'use client'

import { Product } from '@repo/shopify/types'
import { getFromStorage, LOCAL_STORAGE_WISHLIST, setInStorage } from '@repo/storage/service'
import { createContext, ReactNode, useContext, useEffect, useState, useTransition } from 'react'

interface WishlistContextType {
  wishlist: Product['handle'][]
  addToWishlist: (handle: Product['handle']) => void
  removeFromWishlist: (handle: Product['handle']) => void
  handleWishlistToggle: (handle: Product['handle']) => void
  isInWishlist: (handle: Product['handle']) => boolean
  isPending: boolean
  pendingItem: Product['handle'] | null
}

const WishlistContext = createContext<WishlistContextType | undefined>(undefined)

export const WishlistProvider = ({ children }: { children: ReactNode }) => {
  const [isPending, startTransition] = useTransition()
  const [wishlist, setWishlist] = useState<Product['handle'][]>([])
  const [pendingItem, setPendingItem] = useState<Product['handle'] | null>(null)

  // Load wishlist from local storage.
  useEffect(() => {
    const storedWishlist = getFromStorage<Product['handle'][]>(LOCAL_STORAGE_WISHLIST, false, true) ?? []

    setWishlist(Array.isArray(storedWishlist) ? storedWishlist : [])
  }, [])

  // Save wishlist to local storage.
  useEffect(() => {
    setInStorage<Product['handle'][]>(LOCAL_STORAGE_WISHLIST, wishlist, false, true)
  }, [wishlist])

  const addToWishlist = (handle: Product['handle']) => {
    setWishlist((prev) => (prev.includes(handle) ? prev : [...prev, handle]))
  }

  const removeFromWishlist = (handle: Product['handle']) => {
    setWishlist((prev) => prev.filter((id) => id !== handle))
  }

  const isInWishlist = (handle: Product['handle']) => wishlist.includes(handle)

  const handleWishlistToggle = (handle: Product['handle']) => {
    setPendingItem(handle)

    startTransition(() => {
      setWishlist((prev) => {
        const exists = prev.includes(handle)

        return exists ? prev.filter((id) => id !== handle) : [...prev, handle]
      })

      setPendingItem(null)
    })
  }

  return (
    <WishlistContext.Provider value={{ wishlist, addToWishlist, removeFromWishlist, handleWishlistToggle, isInWishlist, isPending, pendingItem }}>
      {children}
    </WishlistContext.Provider>
  )
}

export const useWishlist = () => {
  const context = useContext(WishlistContext)

  if (!context) {
    throw new Error('useWishlist must be used within a WishlistProvider')
  }

  return context
}
